import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Upload, CheckCircle, AlertCircle, Shield } from 'lucide-react';

export default function IdCardUploadPage({ onBack, onNext, onComplete }) {
  const [idCardFront, setIdCardFront] = useState(null);
  const [idCardBack, setIdCardBack] = useState(null);
  const [isParsingFront, setIsParsingFront] = useState(false);
  const [isParsingBack, setIsParsingBack] = useState(false);
  const [parsedData, setParsedData] = useState({ name: '', idNumber: '', validUntil: '' });
  const frontParsedRef = useRef(false);
  const backParsedRef = useRef(false);

  const handleIdCardUpload = (type, file) => {
    if (type === 'front') {
      setIdCardFront(file);
    } else {
      setIdCardBack(file);
    }
  };

  useEffect(() => {
    if (idCardFront && !frontParsedRef.current) {
      frontParsedRef.current = true;
      setIsParsingFront(true);
      setTimeout(() => {
        setParsedData(prev => ({ ...prev, name: '张三', idNumber: '340123199001011234' }));
        setIsParsingFront(false);
      }, 1000);
    }
  }, [idCardFront]);

  useEffect(() => {
    if (idCardBack && !backParsedRef.current) {
      backParsedRef.current = true;
      setIsParsingBack(true);
      setTimeout(() => {
        setParsedData(prev => ({ ...prev, validUntil: '2030-01-01' }));
        setIsParsingBack(false);
      }, 1000);
    }
  }, [idCardBack]);

  const canSubmit = idCardFront && idCardBack && parsedData.name && parsedData.idNumber && parsedData.validUntil;

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">上传身份证</span>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={20} />
            <span className="font-bold">安全提示</span>
          </div>
          <p className="text-sm text-blue-100 leading-relaxed">
            为保障交易安全，根据国家法律法规要求，您需要进行实名认证。您的信息将严格保密，仅用于身份验证。
          </p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">上传身份证照片</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">身份证正面</label>
              <div
                onClick={() => document.getElementById('idCardFrontInput').click()}
                className={`relative h-40 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                  idCardFront
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <input
                  id="idCardFrontInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleIdCardUpload('front', e.target.files[0])}
                />
                {idCardFront ? (
                  <>
                    <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={14} className="text-white" />
                    </div>
                    <img
                      src={URL.createObjectURL(idCardFront)}
                      alt="身份证正面"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                      <Upload size={24} className="text-blue-500" />
                    </div>
                    <span className="text-sm text-slate-600">点击上传身份证正面</span>
                    <span className="text-xs text-slate-400 mt-1">支持 JPG、PNG 格式</span>
                  </>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">身份证反面</label>
              <div
                onClick={() => document.getElementById('idCardBackInput').click()}
                className={`relative h-40 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                  idCardBack
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <input
                  id="idCardBackInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleIdCardUpload('back', e.target.files[0])}
                />
                {idCardBack ? (
                  <>
                    <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle size={14} className="text-white" />
                    </div>
                    <img
                      src={URL.createObjectURL(idCardBack)}
                      alt="身份证反面"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                      <Upload size={24} className="text-blue-500" />
                    </div>
                    <span className="text-sm text-slate-600">点击上传身份证反面</span>
                    <span className="text-xs text-slate-400 mt-1">支持 JPG、PNG 格式</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {(isParsingFront || isParsingBack) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
              <h3 className="font-bold text-slate-800">
                {isParsingFront ? '正在识别身份证正面...' : '正在识别身份证反面...'}
              </h3>
            </div>
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        )}

        {(parsedData.name || parsedData.idNumber || parsedData.validUntil) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-5 bg-gradient-to-b from-green-400 to-green-600 rounded-full" />
              <h3 className="font-bold text-slate-800">识别结果</h3>
            </div>

            <div className="space-y-3">
              {parsedData.name && (
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-500">姓名</span>
                  <span className="text-sm font-bold text-slate-800">{parsedData.name}</span>
                </div>
              )}
              {parsedData.idNumber && (
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-500">身份证号</span>
                  <span className="text-sm font-bold text-slate-800">{parsedData.idNumber}</span>
                </div>
              )}
              {parsedData.validUntil && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-slate-500">有效期至</span>
                  <span className="text-sm font-bold text-slate-800">{parsedData.validUntil}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700 leading-relaxed">
              请确保身份证照片清晰、完整，无遮挡。系统将自动识别您的身份信息，请核对无误后继续。
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white border-t border-slate-200 p-4 pointer-events-auto">
          <button
            disabled={!canSubmit}
            onClick={() => onComplete(parsedData)}
            className={`w-full py-3.5 rounded-xl font-bold text-white shadow-lg transition-all ${
              canSubmit
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl active:scale-[0.98]'
                : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            完成认证
          </button>
        </div>
      </div>
    </div>
  );
}
